"use client";

import { Muted } from "@nyntax/design-system/components/typography/muted";
import {
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
} from "@nyntax/design-system/components/ui/select";
import { cn } from "@nyntax/design-system/lib/utils";
import { useMemo } from "react";
import { useDataTable } from "./data-table-context";

interface DataTableRangeInfoProps {
  className?: string;
  /** Label prefix shown before the range selector (default: "Viewing") */
  label?: string;
  /** Label suffix shown after the total count (default: "results") */
  suffix?: string;
}

function DataTableRangeInfo({
  className,
  label = "Viewing",
  suffix = "results",
}: DataTableRangeInfoProps) {
  const table = useDataTable();
  const pageCount = table.getPageCount();
  const pageSize = table.getState().pagination.pageSize;
  const pageIndex = table.getState().pagination.pageIndex;
  const rowCount = table.getRowCount();

  const pageOptions = useMemo(
    () =>
      Array.from({ length: pageCount }, (_, i) => {
        const start = i * pageSize + 1;
        const end = i < pageCount - 1 ? (i + 1) * pageSize : rowCount;
        return { label: `${start}-${end}`, value: i + 1 };
      }),
    [pageCount, pageSize, rowCount]
  );

  if (rowCount === 0) {
    return null;
  }

  return (
    <div className={cn("flex items-center gap-4 whitespace-nowrap", className)}>
      <Muted as="span">{label}</Muted>
      <div className="flex items-center gap-2">
        <Select
          items={pageOptions}
          onValueChange={(value) => {
            table.setPageIndex((value as number) - 1);
          }}
          value={pageIndex + 1}
        >
          <SelectTrigger
            aria-label="Select result range"
            className="-ml-2 w-fit min-w-fit"
            size="md"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectPopup>
            {pageOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectPopup>
        </Select>
        <Muted as="span">
          of <strong className="font-medium text-foreground">{rowCount}</strong>{" "}
          {suffix}
        </Muted>
      </div>
    </div>
  );
}

export type { DataTableRangeInfoProps };
export { DataTableRangeInfo };
